import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    LOCALE_ID,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {
    AccountTicketsNgrxFacade,
    ApplicationFacade,
    FavoritesFacade,
    FavoriteTicket,
    ProductsFacade,
    ProductsService,
    Ticket,
    TicketsService,
    TicketStatus,
    Translation,
    TravelCardType
} from '@ridango/components';
import {
    AppStartupService,
    AuthService,
    BaseComponent,
    CoreFacade,
    Language,
    NetworkService,
    TranslateObjectFieldPipe
} from '@ridango/core';
import { Temporal } from '@js-temporal/polyfill';
import { map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { ModalController, NavController } from '@ionic/angular';
import { FormBuilder } from '@angular/forms';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import Env from '@ridango/core/env';
import { TicketDetailsZoneMapComponent } from './ticket-details-zone-map/ticket-details-zone-map.component';
import { BreakpointObserver } from '@angular/cdk/layout';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { ActivatedRoute, Router } from '@angular/router';
import { CardType } from '@ridango/components/lib/purchase/purchase.model';
import { AnalyticsService } from '@ridango/core/lib/services/analytics.service';
import { TrackedEventCategory } from '@ridango/core/lib/enums/analytics/tracked-event-categories.enum';
import { TrackedEventAction } from '@ridango/core/lib/enums/analytics/tracked-event-action.enum';
import { ScreenOrientationService } from '@ridango/core/lib/services/screenOrientation.service';
import { DateCalculationService } from '../../services/date-calculation.service';
import {
    TicketActionsService,
    TicketCtx,
    TicketData,
    UiAction
} from '../../services/ticket-actions/ticket-actions.service';
import { TranslateService } from '@ngx-translate/core';
import { ProductType } from '@ridango/components/lib/products/products.model';
import {
    fixedValidityRangeOptionsFromAppStartup,
    ridangoDateTimeValidityOptions,
    resolveFixedValidityRange,
    useUnifiedTicketValidityDisplay
} from '../../services/ticket-validity-display.helper';

@Component({
    selector: 'rwidget-ticket-details',
    templateUrl: './ticket-details.component.html',
    styleUrls: ['./ticket-details.component.scss']
})
export class TicketDetailsComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
    @Input() ticket: Ticket;
    @Input() isHistoryTicket = false;
    @Input() parentRef: HTMLElement;
    @Input() isRecentlyUsed?: boolean;
    @Output() refreshTickets: EventEmitter<void> = new EventEmitter();
    @Output() handleBuyAgain: EventEmitter<Ticket> = new EventEmitter();
    @ViewChild('aztecContainer') aztecContainer: ElementRef;

    protected readonly TravelCardType = TravelCardType;
    timezone: string;
    formattedDate: string;
    dateformat: string;
    dateTimeFormat: string;
    primaryDateTimeFormat: string;
    expireDateTimeFormat: string;
    currency: string;
    assetsPath$ = this.applicationFacade.assetsPath$;
    favoriteTickets: FavoriteTicket[] = [];
    readonly RETURN_TICKET = 'RETURN_TICKET';
    readonly LEND_TICKET = 'LEND_TICKET';
    readonly RETURN_LEND_TICKET = 'RETURN_LEND_TICKET';
    readonly SEND_TICKET = 'SEND_TICKET';
    readonly BUY_TICKET_AGAIN = 'BUY_TICKET_AGAIN';
    readonly ADD_TO_FAVORITE_TICKET = 'ADD_TO_FAVORITE_TICKET';
    readonly TEN_TRIP_TICKET_UNITS = 10;
    private readonly TICKET_DETAILS = 'Ticket details';
    private readonly ZONE_MAP = 'Zone map';
    private lastProducts: Ticket['product'][] = [];
    private readonly ticketSubject$ = new BehaviorSubject<Ticket | undefined>(undefined);
    private readonly ticket$ = this.ticketSubject$.asObservable();
    public adjustedEndingDate$: Observable<string | undefined>;
    public isOnline$ = this.networkService.getStatus();
    public ticketBtns: UiAction[] = [];
    public ticketTitle$: Observable<string>;
    public translate: TranslateService;
    public showQrCode = false;
    public showTravellerQrTicketSummary = false;
    public isTopUp = true;
    public isTopUp$: Observable<boolean>;
    public cardType = CardType;

    constructor(
        @Inject('ENV') public env: Env,
        @Inject(LOCALE_ID) public locale: string,
        protected modalCtrl: ModalController,
        protected formBuilder: FormBuilder,
        protected accountTicketsFacade: AccountTicketsNgrxFacade,
        protected applicationFacade: ApplicationFacade,
        protected coreFacade: CoreFacade,
        protected appStartupService: AppStartupService,
        public ticketService: TicketsService,
        protected bpObservable: BreakpointObserver,
        protected favoritesFacade: FavoritesFacade,
        protected authService: AuthService,
        protected router: Router,
        protected activatedRoute: ActivatedRoute,
        protected translateService: TranslateService,
        protected networkService: NetworkService,
        protected analyticsService: AnalyticsService,
        protected screenOrientationService: ScreenOrientationService,
        protected dateCalculationService: DateCalculationService,
        protected ticketActionsService: TicketActionsService,
        protected nav: NavController,
        public translatePipe: TranslateObjectFieldPipe,
        protected productsFacade: ProductsFacade,
        protected productsService: ProductsService
    ) {
        super();
        this.timezone = appStartupService.getTimezone();
        this.currency = appStartupService.getCurrency();
        this.dateformat = env.PRIMARY_DATE_FORMAT;
        this.dateTimeFormat = env.PERIOD_TICKET_DATE_FORMAT;
        this.primaryDateTimeFormat = env.PRIMARY_DATE_TIME_FORMAT;
        this.expireDateTimeFormat = env.TICKET_EXPIRES_DATE_FORMAT;
        this.translate = translateService;
    }

    async ngOnInit(): Promise<void> {
        this.updateQrTicketFlags();
        this.ticketTitle$ = this.getTicketTitle();
        this.updateAdjustedEndingDate();
        this.isTopUp$ = this.productsFacade.all$.pipe(
            switchMap((products) => {
                if (!products || products.length === 0) {
                    return this.productsService.fetchProductsByChannel();
                }
                return of(products);
            }),
            map((products) => {
                this.lastProducts = products || [];
                this.attachProductToTicket(products);
                const productType = this.ticket?.product?.product_type || this.ticket?.product_type;
                return productType === ProductType.WalletProduct;
            }),
            startWith(true)
        );

        this.isTopUp$.pipe(takeUntil(this.componentDestroyed$)).subscribe((isTopUp) => {
            this.isTopUp = isTopUp;
            this.createTicketActions();
        });

        try {
            await this.screenOrientationService.lock('portrait-primary');
        } catch (error) {
            // Screen orientation API not available (browser environment)
        }

        if (Capacitor.isNativePlatform()) {
            await KeepAwake.keepAwake();
        }

        const isAuthenticated = this.authService.getLoginStatus();

        if (this.env.SHOW_FAVORITE_TICKETS && isAuthenticated) {
            this.favoritesFacade.getFavoriteTickets();

            this.favoritesFacade.favoriteTickets$.pipe(takeUntil(this.componentDestroyed$)).subscribe((tickets) => {
                this.favoriteTickets = tickets;
                this.createTicketActions();
            });
        }

        if (this.env.SHOW_SELECTOR_TIME_LEND_TICKET) {
            const date: string = this.ticket?.loanedUntil?.toString();
            if (!date) return;
            this.formattedDate = date.replace('T', ' ');
        }

        this.bpObservable
            .observe(['(orientation: landscape)', '(orientation: portrait)'])
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((result) => {
                if (result.matches) {
                    this.parentRef?.scrollIntoView?.({
                        behavior: 'smooth',
                        block: 'start',
                        inline: 'nearest'
                    });
                }
            });
    }

    private updateQrTicketFlags(): void {
        this.showQrCode = this.shouldShowQrCode(this.ticket);
        this.showTravellerQrTicketSummary = this.shouldShowTravellerQrTicketSummary(this.ticket);
    }

    ngOnChanges(changes: SimpleChanges) {
        this.createTicketActions();
        if (changes.ticket) {
            this.attachProductToTicket(this.lastProducts);
            this.ticketSubject$.next(this.ticket);
            this.ticketTitle$ = this.getTicketTitle();
            this.updateQrTicketFlags();
        }
    }

    ngAfterViewInit(): void {
        // Scroll to the top of the page when the component is initially loaded
        if (this.bpObservable.isMatched('(orientation: landscape)')) {
            this.parentRef?.scrollIntoView?.({
                behavior: 'smooth',
                block: 'start',
                inline: 'nearest'
            });
        }
    }

    async ngOnDestroy(): Promise<void> {
        if (Capacitor.isNativePlatform()) {
            await KeepAwake.allowSleep();
        }
        await this.screenOrientationService.unlock();
    }

    get showQrTicket() {
        return this.showQrCode && !this.isCanceledTicket && (!this.env.HIDE_HISTORY_CODE || !this.isHistoryTicket);
    }

    get isAuth(): boolean {
        return this.authService.getLoginStatus();
    }

    getTicketTitle(): Observable<string> {
        if (this.env.APP_NAME === 'orebro' || this.env.APP_NAME === 'varmland') {
            const ticketNameTranslations = Object.entries(this.ticket.names).map(([lang, translation]) => ({
                lang,
                translation
            }));

            return (
                this.ticket &&
                this.coreFacade.selectedLanguage$.pipe(
                    map((lang: Language) => {
                        const prodNameTranslations = this.translatePipe.transform(
                            { translations: ticketNameTranslations },
                            null,
                            lang.code
                        );
                        const prodNameTranslation = Object.values(prodNameTranslations)[0];
                        return prodNameTranslation;
                    })
                )
            );
        } else {
            return this.coreFacade.selectedLanguage$.pipe(
                map((lang: Language) => {
                    const productTranslation = this.ticket.productTranslations?.find(
                        (translation: Translation) => translation.lang === lang?.code
                    );
                    return productTranslation?.translation ?? this.ticket.productName;
                })
            );
        }
    }

    translateString(inputObject) {
        if (!inputObject) return '-';
        if (Object.prototype.hasOwnProperty.call(inputObject, 'nameTranslations')) {
            const translations = inputObject.nameTranslations?.translations;
            if (!translations) return '-';

            return translations[this.translate.currentLang] || translations[Object.keys(translations)[0]] || '-';
        } else if (inputObject.name?.translations) {
            const translations = inputObject.name.translations;
            return translations[this.translate.currentLang] || translations[Object.keys(translations)[0]] || '-';
        } else {
            return '-';
        }
    }

    private createTicketActions(): void {
        this.ticketBtns = this.ticketActionsService.createActionsList(
            this.buildCtx(),
            this.TICKET_DETAILS,
            'onDetails'
        );
    }

    isFavoriteTicket() {
        return !!this.favoriteTickets?.find((t) => t.productId === this.ticket?.productId);
    }

    isTicketRefunded(): boolean {
        return this.ticket.status === TicketStatus.Returned;
    }

    async openZoneMap() {
        this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
            category: TrackedEventCategory.TICKETS,
            name: `${this.TICKET_DETAILS} - ${this.ZONE_MAP}`
        });

        const modal = await this.modalCtrl.create({
            component: TicketDetailsZoneMapComponent,
            cssClass: 'ticket-details-zone-map-modal'
        });

        await modal.present();
    }

    get isCanceledTicket(): boolean {
        return this.ticket.status?.trim() === 'N';
    }

    qrClick() {
        this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
            category: TrackedEventCategory.TICKETS,
            name: `${this.TICKET_DETAILS} - QR`
        });

        if (this.env.ZOOMABLE_QR_CODE) {
            this.router.navigate(['qr'], { relativeTo: this.activatedRoute });
        }
    }

    isManualActivationEnabled(ticket): boolean {
        return !this.isTopUp && this.env.TICKET_MANUAL_ACTIVATION_ALLOWED && !ticket.validated;
    }

    private updateAdjustedEndingDate(): void {
        this.ticketSubject$.next(this.ticket);
        this.adjustedEndingDate$ = this.dateCalculationService.getAdjustedEndingDate$(this.ticket$, this.timezone);
    }

    private buildCtx(): TicketCtx {
        return {
            ticket: this.ticket as TicketData,
            ticketTitle$: this.ticketTitle$,
            env: this.env,
            isHistory: this.isHistoryTicket,
            isAuth: this.authService.getLoginStatus(),
            locale: this.locale,
            isFavourite: this.isFavoriteTicket(),
            refresh: this.refreshTickets,
            goBack: () => this.nav.back(),
            isTopUp: this.isTopUp
        };
    }

    /**
     * Enriches ticket with full product details from the products store.
     * Required to determine product_type for datetime formatting decisions.
     */
    private attachProductToTicket(products?: Ticket['product'][]): void {
        if (!this.ticket || !products?.length) {
            return;
        }

        const ticketProductId = this.ticket.productId;
        const strictMatched = products.find((p) => p?.product_id === ticketProductId);
        const looseMatched =
            strictMatched || products.find((p) => String(p?.product_id ?? '') === String(ticketProductId ?? ''));

        const matched = strictMatched || looseMatched;
        if (!matched) return;

        this.ticket.product = matched;
        if (!this.ticket.product_type && matched.product_type) {
            this.ticket.product_type = matched.product_type;
        }
    }

    protected shouldShowQrCode(ticket: Ticket): boolean {
        if (!ticket) {
            return false;
        }

        const { status, initialUnits, units, bookletEnding, ending, expiredAt } = ticket;

        const ticketStatus = status?.trim();

        if (this.isQrStatusExcluded(ticketStatus)) {
            return false;
        }

        if (this.isExpired(bookletEnding)) {
            return false;
        }

        const isMultiUnitTicket = this.isMultiUnitTicket(initialUnits);
        const shouldCheckTicketEnding = !isMultiUnitTicket || units === 0;

        if (shouldCheckTicketEnding && this.isExpired(ending || expiredAt)) {
            return false;
        }

        if (ticketStatus === TicketStatus.CONFIRMED && this.env.TICKET?.HIDE_QR_FOR_INACTIVE_TICKETS) {
            return false;
        }

        return true;
    }

    private isQrStatusExcluded(status?: string): boolean {
        const excludedStatuses = [
            TicketStatus.EXPIRED,
            TicketStatus.CANCELLED,
            TicketStatus.Refunded.trim(),
            TicketStatus.Returned.trim(),
            TicketStatus.LENT
        ];

        return excludedStatuses.includes(status);
    }

    private isMultiUnitTicket(initialUnits?: number): boolean {
        return initialUnits != null && initialUnits > 1;
    }

    private isExpired(date?: Date | string): boolean {
        if (!date) {
            return false;
        }

        const expiration = this.parseTicketDate(date);
        const now = Temporal.Now.zonedDateTimeISO(this.timezone).toInstant();

        return Temporal.Instant.compare(now, expiration) > 0;
    }

    private parseTicketDate(date: Date | string): Temporal.Instant {
        if (date instanceof Date) {
            return Temporal.Instant.from(date.toISOString());
        }
        const hasOffset = date.includes('+') || date.includes('Z');
        return hasOffset
            ? Temporal.Instant.from(date)
            : Temporal.ZonedDateTime.from(`${date}[${this.timezone}]`).toInstant();
    }

    get useUnifiedTicketValidityDisplay(): boolean {
        return useUnifiedTicketValidityDisplay(this.env);
    }

    ridangoDateTimeOptions(ticket: Ticket) {
        return ridangoDateTimeValidityOptions(this.env, ticket);
    }

    standardTicketRangeStart(ticket: Ticket) {
        return this.fixedValidityRangeStart(ticket) || ticket.starting || ticket.timestamp;
    }

    standardTicketRangeEnd(ticket: Ticket) {
        return this.fixedValidityRangeEnd(ticket) || ticket.ending || ticket.bookletEnding || ticket.expiredAt;
    }

    bookletRangeStart(ticket: Ticket) {
        return this.fixedValidityRangeStart(ticket) || ticket.timestamp || ticket.starting;
    }

    bookletRangeEnd(ticket: Ticket) {
        return this.fixedValidityRangeEnd(ticket) || ticket.bookletEnding || ticket.ending || ticket.expiredAt;
    }

    hasFixedValidityRange(ticket: Ticket): boolean {
        return Boolean(
            resolveFixedValidityRange(ticket, fixedValidityRangeOptionsFromAppStartup(this.appStartupService))
        );
    }

    fixedValidityRangeStart(ticket: Ticket): Date | undefined {
        return resolveFixedValidityRange(ticket, fixedValidityRangeOptionsFromAppStartup(this.appStartupService))
            ?.start;
    }

    fixedValidityRangeEnd(ticket: Ticket): Date | undefined {
        return resolveFixedValidityRange(ticket, fixedValidityRangeOptionsFromAppStartup(this.appStartupService))?.end;
    }

    /**
     * Determines if ticket should display full datetime format instead of date-only format.
     * M (minute-based) and H (hour-based) tickets show time, other types show only date.
     */
    shouldUseDateTimeForQrTicket(ticket: Ticket): boolean {
        const productType = ticket?.product?.product_type || ticket?.product_type;
        return productType === 'M' || productType === 'H';
    }

    get ticketPriceForDisplay(): number | null {
        if (this.ticket == null) return null;
        const price = this.ticket.price;
        if (price != null) {
            return typeof price === 'object' ? price.amount : price;
        }
        if (this.ticket.basePrice != null) return this.ticket.basePrice;
        return null;
    }

    private shouldShowTravellerQrTicketSummary(ticket: Ticket): boolean {
        if (!ticket) {
            return false;
        }
        const { starting, ending, initialUnits, units } = ticket;
        if (!starting) {
            return true;
        }
        const hasRemainingUnits = initialUnits > 1 && units > 0 && units < initialUnits;
        return hasRemainingUnits && this.isExpired(ending);
    }
}
